import { useEffect, useMemo, useState } from "react";
import ReactJson from "react-json-view";
import Modal from "../modal/Modal";
import { Alert, buildComponentsByName, getRateSet, summarizeRateWarnings, summarizeComponentWarnings } from "./BillingRateBuilderShared";
import BillingRateBuilderSubmitResults from "./BillingRateBuilderSubmitResults";
import {
    summarizeComponentValidation,
    summarizeRateValidation,
} from "./billingRateBuilderSchemas";
import {
    fetchGetApiRequestJobStatus,
    fetchHrpSubmissionTarget,
    submitBillingRateBatchJob,
    submitBillingRateAudit,
    updateBillingRateSubmission,
    checkBillingRateComponentCollisions,
    fetchAccounts,
    fetchBenefitPlansByAccountKey,
    fetchBillingRateByBenefitPlanKey,
} from "../../utils/apiUtils.js";
import { delay } from "../../utils/commonUtils";
import {
    applyPreSubmitComponentCollisionAmendments,
    applyPreSubmitRateCollisionAmendments,
    buildBatchSubmitAuditPayload,
    buildSubmitResultByPlan,
    extractBillingRateAccountHccId,
    formatSubmitOutcomeSummary,
    parseJobResponseBody,
    runBillingRateBatchSubmission,
    shouldPreserveBillingRateDraft,
    SUBMIT_BILLING_RATE_BATCH_SK,
} from "../../utils/billingRateUtils";

const step = 4;

const buildValidationAlertProps = (
    validation,
    { singular, plural, capitalized },
    isEmpty,
    hasWarnings = false,
) => {
    if (isEmpty) {
        return {
            variant: "warning",
            title: `No ${plural} exist`,
            footer: `${capitalized} must be created in order to submit to HRP.`,
        };
    }
    if (validation.hasErrors) {
        return {
            variant: "error",
            title: `Errors detected in ${validation.invalidCount} ${
                validation.invalidCount === 1 ? singular : plural
            } payload${validation.invalidCount === 1 ? "" : "s"}`,
            body: validation.bodyLines,
            footer: "Please correct these errors in order to submit to HRP.",
        };
    }
    if (!hasWarnings) {
        return {
            variant: "success",
            title: `All ${singular} payloads passed validation`,
            footer: `No validation errors were found. ${capitalized} payloads are ready for submission to HRP.`,
        };
    }
    return null;
};

const EXPORT_VERSION = "1.0";

const BillingRateBuilderStepFour = ({
    activeStep,
    componentInputs,
    rateInputs,
    componentPayloads,
    ratePayloads,
    fees,
    uploadContext,
    billingCategoryRows,
    plans,
    componentCollisions,
    rateCollisions,
    submissionRecord,
    onHrpSubmitted,
    onClearDraft,
    onPreserveDraft,
    onBackToHome,
    groupId,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSummary, setSubmitSummary] = useState(null);
    const [submitResponseBody, setSubmitResponseBody] = useState(null);
    const [submittedBatch, setSubmittedBatch] = useState(null);
    const [jobRef, setJobRef] = useState(null);
    const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
    const [resultsViewMode, setResultsViewMode] = useState("details");

    const componentValidation = useMemo(
        () => summarizeComponentValidation(componentPayloads),
        [componentPayloads],
    );

    const rateValidation = useMemo(
        () => summarizeRateValidation(ratePayloads),
        [ratePayloads],
    );

    const componentWarnings = useMemo(
        () => summarizeComponentWarnings(componentCollisions, componentPayloads),
        [componentCollisions, componentPayloads],
    );

    const componentsByName = useMemo(
        () => buildComponentsByName(componentPayloads),
        [componentPayloads],
    );

    const rateWarnings = useMemo(
        () => summarizeRateWarnings(ratePayloads, plans, componentsByName, fees, uploadContext, rateCollisions),
        [ratePayloads, plans, componentsByName, fees, uploadContext, rateCollisions],
    );

    const tieredNonZeroDefaultWarnings = useMemo(
        () => (componentPayloads ?? []).flatMap((wrapper) => {
            if (!wrapper?.tiered) return [];
            const rateSet = getRateSet(wrapper?.billingRateComponentPayload?.billingRateComponent);
            const defaultAmount = rateSet?.defaultRate?.amount;
            if (defaultAmount == null || defaultAmount === "0.00") return [];
            const name = wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName ?? "Unknown component";
            return [`${name}: default rate is $${defaultAmount} but the component is tiered (expected 0.00)`];
        }),
        [componentPayloads],
    );

    const submitResultByPlan = useMemo(
        () => buildSubmitResultByPlan(submitResponseBody, {
            components: submittedBatch?.components ?? [],
            rates: submittedBatch?.rates ?? [],
            ratePayloads: submittedBatch?.ratePayloads ?? ratePayloads ?? [],
            componentPayloads: submittedBatch?.componentPayloads ?? componentPayloads ?? [],
            billingCategoryRows,
        }),
        [
            submitResponseBody,
            submittedBatch,
            ratePayloads,
            componentPayloads,
            billingCategoryRows,
        ],
    );

    const submitResponseJson = useMemo(
        () => parseJobResponseBody(submitResponseBody),
        [submitResponseBody],
    );

    const resetSubmitFeedback = () => {
        setSubmitError(null);
        setSubmitSummary(null);
        setSubmitResponseBody(null);
        setSubmittedBatch(null);
        setJobRef(null);
        setIsResultsModalOpen(false);
        setResultsViewMode("details");
    };

    // Clear stale results when the upstream payloads change (e.g. user edits in Step 2/3
    // and returns to Step 4). Keyed on lengths to avoid resetting on identical-shape rerenders.
    useEffect(() => {
        setSubmitError(null);
        setSubmitSummary(null);
        setSubmitResponseBody(null);
        setSubmittedBatch(null);
        setJobRef(null);
        setIsResultsModalOpen(false);
        setResultsViewMode("details");
    }, [componentInputs.length, rateInputs.length]);

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        resetSubmitFeedback();
        try {
            const accountHccId = groupId
                ?? extractBillingRateAccountHccId({ ratePayloads, componentPayloads });
            const { componentInputs: collisionCheckedComponents, componentPayloads: collisionCheckedComponentPayloads } =
                await applyPreSubmitComponentCollisionAmendments({
                    componentPayloads,
                    groupId: accountHccId,
                    checkBillingRateComponentCollisions: checkBillingRateComponentCollisions,
                });
            const { rateInputs: collisionCheckedRates, ratePayloads: collisionCheckedRatePayloads } =
                await applyPreSubmitRateCollisionAmendments({
                    ratePayloads,
                    accountHccId,
                    fetchAccounts,
                    fetchBenefitPlansByAccountKey,
                    fetchBillingRateByBenefitPlanKey,
                });
            const { jobRef: completedJobRef, responseBody, summary } =
                await runBillingRateBatchSubmission({
                    submitBatchJob: submitBillingRateBatchJob,
                    fetchJobStatus: fetchGetApiRequestJobStatus,
                    delay,
                    components: collisionCheckedComponents,
                    rates: collisionCheckedRates,
                });
            setJobRef(completedJobRef);
            setSubmitResponseBody(responseBody);
            setSubmitSummary(summary);
            setSubmittedBatch({
                components: collisionCheckedComponents,
                rates: collisionCheckedRates,
                ratePayloads: collisionCheckedRatePayloads,
                componentPayloads: collisionCheckedComponentPayloads,
            });
            const preserveDraft = shouldPreserveBillingRateDraft({ hasIssues: summary.hasIssues });
            if (!preserveDraft && submissionRecord?.sk) {
                updateBillingRateSubmission({ sk: submissionRecord.sk }).catch((err) => {
                    console.warn("[billing-rate] failed to persist submitted status:", err);
                });
            }
            submitBillingRateAudit(buildBatchSubmitAuditPayload({
                submissionId: submissionRecord?.sk ?? null,
                clientName: submissionRecord?.clientName ?? null,
                jobRef: completedJobRef,
                responseBody,
                summary,
                componentCount: collisionCheckedComponents.length,
                rateCount: collisionCheckedRates.length,
            }));
            onHrpSubmitted?.({ hasIssues: summary.hasIssues });
            if (preserveDraft) {
                onPreserveDraft?.();
            } else {
                onClearDraft?.();
            }
        } catch (e) {
            const err = e instanceof Error ? e : new Error(String(e));
            const rawCause = err.cause;
            const causeObj =
                rawCause && typeof rawCause === "object" && !Array.isArray(rawCause)
                    ? rawCause
                    : null;
            submitBillingRateAudit(buildBatchSubmitAuditPayload({
                submissionId: submissionRecord?.sk ?? null,
                clientName: submissionRecord?.clientName ?? null,
                jobRef: causeObj?.pk
                    ? { pk: causeObj.pk, sk: causeObj.sk ?? SUBMIT_BILLING_RATE_BATCH_SK }
                    : null,
                responseBody: causeObj?.responseBody ?? null,
                componentCount: componentInputs.length,
                rateCount: rateInputs.length,
                failed: true,
                errorMessage: err?.message ?? String(err),
            }));
            onPreserveDraft?.();
            setSubmitError({
                message: err.message || "Request failed",
                sent: { componentInputs, rateInputs },
                cause:
                    causeObj ??
                    (typeof rawCause === "string" || typeof rawCause === "number"
                        ? rawCause
                        : undefined),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleExport = () => {
        const payload = {
            version: EXPORT_VERSION,
            exportedAt: new Date().toISOString(),
            componentPayloads,
            ratePayloads,
            fees,
            plans,
            uploadContext,
            componentCollisions,
            rateCollisions,
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const clientName = submissionRecord?.clientName
            ? `_${submissionRecord.clientName.replace(/\s+/g, "_")}`
            : "";
        a.href = url;
        a.download = `billing_rate_builder${clientName}_${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const componentsEmpty = (componentPayloads?.length ?? 0) === 0;
    const ratesEmpty = (ratePayloads?.length ?? 0) === 0;

    const componentValidationAlertProps = buildValidationAlertProps(
        componentValidation,
        { singular: "component", plural: "components", capitalized: "Component" },
        componentsEmpty,
        componentWarnings.hasWarnings,
    );
    const rateValidationAlertProps = buildValidationAlertProps(
        rateValidation,
        { singular: "rate", plural: "rates", capitalized: "Rate" },
        ratesEmpty,
        rateWarnings.hasWarnings,
    );

    const canSubmit =
        !isSubmitting &&
        !componentsEmpty &&
        !ratesEmpty &&
        !componentValidation.hasErrors &&
        !rateValidation.hasErrors;

    const submitButtonLabel = isSubmitting ? "Submitting\u2026" : "Submit to HRP";

    if (activeStep !== step) return null;

    return (
        <div className="billing-rate-builder__step container">
            {componentValidationAlertProps && (
                <Alert {...componentValidationAlertProps} />
            )}
            {rateValidationAlertProps && (
                <Alert {...rateValidationAlertProps} />
            )}
            {!componentsEmpty && componentWarnings.hasWarnings && (
                <Alert
                    variant="warning"
                    title={`Warnings detected in ${componentWarnings.warningCount} component payload${
                        componentWarnings.warningCount === 1 ? "" : "s"
                    }`}
                    body={componentWarnings.warningLines}
                    footer="It's recommended to investigate these warnings before submitting to HRP."
                />
            )}
            {!ratesEmpty && rateWarnings.hasWarnings && (
                <Alert
                    variant="warning"
                    title={`Warnings detected in ${rateWarnings.warningCount} rate payload${
                        rateWarnings.warningCount === 1 ? "" : "s"
                    }`}
                    body={rateWarnings.warningLines}
                    footer="It's recommended to investigate these warnings before submitting to HRP."
                />
            )}

            {tieredNonZeroDefaultWarnings.length > 0 && (
                <Alert
                    variant="warning"
                    title={`${tieredNonZeroDefaultWarnings.length} tiered component${tieredNonZeroDefaultWarnings.length === 1 ? "" : "s"} with a non-zero default rate`}
                    body={tieredNonZeroDefaultWarnings}
                    footer="Tiered components normally use a default rate of 0.00. Verify this is intentional before submitting to HRP."
                />
            )}

            <div className="billing-rate-builder__actions">
                <button
                    type="button"
                    className="btn edit-btn billing-rate-builder__submit-btn"
                    onClick={onBackToHome}
                >
                    {'\u2190'} Back to Submissions
                </button>
                <button
                    type="button"
                    className="btn edit-btn billing-rate-builder__submit-btn"
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                >
                    {submitButtonLabel}
                </button>
                <button
                    type="button"
                    className="btn billing-rate-builder__export-btn"
                    disabled={componentsEmpty && ratesEmpty}
                    onClick={handleExport}
                    title="Download the current builder payload as a JSON file"
                >
                    Export
                </button>
            </div>

            {submitSummary && !submitError && (
                <div
                    className={`billing-rate-builder__alert billing-rate-builder__alert--${
                        submitSummary.hasIssues ? "warning" : "success"
                    }`}
                >
                    <p className="title billing-rate-builder__alert-title">
                        {submitSummary.hasIssues ? "Submitted with issues" : "Success"}
                    </p>
                    <p className="billing-rate-builder__alert-body-plain">
                        <strong>Components:</strong>{" "}
                        {formatSubmitOutcomeSummary(submitSummary.componentOutcomes, {
                            legacySuccessCount: submitSummary.hasOutcomeBreakdown
                                ? undefined
                                : submitSummary.componentChangedCount,
                        })}{" "}
                        in HRP.
                        <br />
                        <strong>Rates:</strong>{" "}
                        {formatSubmitOutcomeSummary(submitSummary.rateOutcomes, {
                            legacySuccessCount: submitSummary.hasOutcomeBreakdown
                                ? undefined
                                : submitSummary.rateChangedCount,
                        })}{" "}
                        in HRP.
                        {(submitSummary.componentOutcomes?.skippedDuplicate > 0
                            || submitSummary.rateOutcomes?.skippedDuplicate > 0) && (
                            <>
                                <br />
                                {[
                                    submitSummary.componentOutcomes?.skippedDuplicate > 0
                                        ? `${submitSummary.componentOutcomes.skippedDuplicate} component${
                                            submitSummary.componentOutcomes.skippedDuplicate === 1 ? "" : "s"
                                        } already in HRP`
                                        : null,
                                    submitSummary.rateOutcomes?.skippedDuplicate > 0
                                        ? `${submitSummary.rateOutcomes.skippedDuplicate} rate${
                                            submitSummary.rateOutcomes.skippedDuplicate === 1 ? "" : "s"
                                        } already in HRP`
                                        : null,
                                ].filter(Boolean).join("; ")}
                                .
                            </>
                        )}
                        {submitSummary.hasIssues && (
                            <>
                                <br />
                                <strong>{submitSummary.componentFailures}</strong> component
                                failure{submitSummary.componentFailures === 1 ? "" : "s"},{" "}
                                <strong>{submitSummary.rateFailures}</strong> rate failure
                                {submitSummary.rateFailures === 1 ? "" : "s"},{" "}
                                <strong>{submitSummary.skippedRates}</strong> rate
                                {submitSummary.skippedRates === 1 ? "" : "s"} skipped due to
                                component dependencies.
                            </>
                        )}
                    </p>
                    {jobRef?.pk && (
                        <p className="billing-rate-builder__submit-success-job billing-rate-builder__alert-body-plain billing-rate-builder__muted">
                            Job pk: <code>{jobRef.pk}</code>
                        </p>
                    )}
                    <div className="billing-rate-builder__submit-success-footer billing-rate-builder__actions">
                        <button
                            type="button"
                            className="btn"
                            onClick={() => {
                                setResultsViewMode("details");
                                setIsResultsModalOpen(true);
                            }}
                        >
                            View submission details
                        </button>
                    </div>
                </div>
            )}

            {submitError && (
                <div className="billing-rate-builder__alert billing-rate-builder__alert--danger">
                    <p className="title billing-rate-builder__alert-title">Submit failed</p>
                    <p className="billing-rate-builder__alert-message">{submitError.message}</p>
                    {submitError.cause != null && (
                        <pre className="billing-rate-builder__pre">
                            {typeof submitError.cause === "string"
                                ? submitError.cause
                                : JSON.stringify(submitError.cause, null, 2)}
                        </pre>
                    )}
                    {submitError.sent != null && (
                        <>
                            <p>Payload sent:</p>
                            <pre className="billing-rate-builder__pre">
                                {JSON.stringify(submitError.sent, null, 2)}
                            </pre>
                        </>
                    )}
                </div>
            )}

            <Modal isOpen={isResultsModalOpen} onClose={() => setIsResultsModalOpen(false)}>
                <div className="billing-rate-builder__submit-results-modal">
                    <p className="title billing-rate-builder__modal-title">Submission results</p>
                    <div
                        className="switch billing-rate-builder__submit-results-switch"
                        role="tablist"
                        aria-label="Submission results view"
                    >
                        <p
                            role="tab"
                            aria-selected={resultsViewMode === "details"}
                            className={`option ${resultsViewMode === "details" ? "active" : ""}`}
                            onClick={() => setResultsViewMode("details")}
                        >
                            Details
                        </p>
                        <p
                            role="tab"
                            aria-selected={resultsViewMode === "raw"}
                            className={`option ${resultsViewMode === "raw" ? "active" : ""}`}
                            onClick={() => setResultsViewMode("raw")}
                        >
                            Raw response
                        </p>
                    </div>
                    {resultsViewMode === "details" ? (
                        <BillingRateBuilderSubmitResults
                            key={jobRef?.pk ?? "submit-results"}
                            planGroups={submitResultByPlan.planGroups}
                            componentsByName={componentsByName}
                        />
                    ) : (
                        Object.keys(submitResponseJson).length > 0 ? (
                            <div className="billing-rate-builder__submit-results-raw-panel">
                                <ReactJson
                                    style={{ padding: "1rem" }}
                                    src={submitResponseJson}
                                    name={false}
                                    collapsed={2}
                                    collapseStringsAfterLength={150}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                    sortKeys={true}
                                />
                            </div>
                        ) : (
                            <p className="billing-rate-builder__list-empty">No response body returned.</p>
                        )
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default BillingRateBuilderStepFour;
